// Copyright (c) 2026 Yunus YILDIZ — SPDX-License-Identifier: BUSL-1.1

import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { Alert } from '@/components/ui/Alert'
import { Spinner } from '@/components/ui/Spinner'
import { ROUTES } from '@/constants/routes'
import { useAuth } from '@/lib/auth/useAuth'
import { clearPortalMsSsoPayload, readPortalMsSsoPayload } from '@/lib/auth/microsoftPortalSso'
import { getErrorMessage } from '@/lib/utils/errorHandler'

export function MicrosoftAuthCompletePage() {
  const { completeMicrosoftSignIn } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [error, setError] = useState('')

  useEffect(() => {
    const flowIdFromUrl = searchParams.get('flowId')
    const pollTokenFromUrl = searchParams.get('pollToken')

    // Prefer URL params (fresh redirect) over any stale sessionStorage entry
    const payload =
      flowIdFromUrl && pollTokenFromUrl
        ? { flowId: flowIdFromUrl, pollToken: pollTokenFromUrl, startedAt: Date.now() }
        : readPortalMsSsoPayload()

    if (!payload) {
      setError('Your Microsoft sign-in session was not found. Please try again from the sign-in page.')
      return
    }

    let cancelled = false
    ;(async () => {
      try {
        await completeMicrosoftSignIn(payload.flowId, payload.pollToken)
        clearPortalMsSsoPayload()
        if (!cancelled) navigate(ROUTES.DASHBOARD, { replace: true })
      } catch (e) {
        clearPortalMsSsoPayload()
        if (!cancelled) setError(getErrorMessage(e))
      }
    })()

    return () => {
      cancelled = true
    }
  }, [completeMicrosoftSignIn, navigate, searchParams])

  return (
    <AuthLayout>
      <div className="mb-6 text-center">
        <h1 className="text-xl font-bold text-ink">Microsoft sign-in</h1>
        <p className="mt-1 text-sm text-ink-muted">Securing your session…</p>
      </div>

      <div className="card flex min-h-[200px] flex-col items-center justify-center gap-4 p-8">
        {error ? (
          <>
            <Alert variant="error">{error}</Alert>
            <Link to={ROUTES.LOGIN} className="text-sm font-medium text-brand hover:underline">
              Back to sign in
            </Link>
          </>
        ) : (
          <>
            <Spinner size="lg" />
            <p className="text-center text-sm text-ink-muted">Completing sign-in with Microsoft…</p>
          </>
        )}
      </div>
    </AuthLayout>
  )
}
